import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

const CommonSelect = React.forwardRef(({
  className,
  label,
  id,
  error,
  required = false,
  options = [],
  placeholder = "Select an option",
  disableAutofill = false,
  loading = false,
  value,
  onChange,
  onValueChange,
  searchable = false,
  onSearchChange,
  searchValue,
  onSearch,
  labelSize = "normal",
  disabled = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  onOpenChange,
  ...props
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const searchTerm = searchValue !== undefined ? searchValue : localSearchQuery;
  const setSearchQuery = onSearchChange || setLocalSearchQuery;


  const handleValueChange = onValueChange || onChange;

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, searchable]);

  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isOpen);
    }
  }, [isOpen, onOpenChange]);

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !loading) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchQuery("");
        if (onSearch) onSearch("");
      }
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (onSearch) onSearch(query);
  };

  const handleOptionClick = (optionValue, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (handleValueChange) {
      handleValueChange(optionValue);
    }
    setIsOpen(false);
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 15) {
      if (hasMore && !loadingMore && onLoadMore) {
        onLoadMore();
      }
    }
  };

  const filteredOptions = searchable && !onSearch
    ? options.filter(opt => opt.label.toString().toLowerCase().includes(searchTerm.toLowerCase()))
    : options;

  return (
    <div className={cn("relative w-full flex flex-col gap-1.5", className)} ref={dropdownRef}>
      {label && (
        <Label
          htmlFor={id}
          className={cn(
            "font-medium tracking-tight transition-colors duration-200",
            labelSize === "small" ? "text-[11px]" : "text-[13px]",
            error ? 'text-destructive' : 'text-foreground/80'
          )}
        >
          {label} {required && <span className="text-destructive">*</span>}
        </Label>
      )}

      <div className="relative">
        <button
          type="button"
          id={id}
          onClick={handleToggle}
          disabled={disabled || loading}
          className={cn(
            "flex w-full items-center justify-between border border-input bg-background px-3.5 py-2 text-sm transition-all focus:outline-none rounded-lg",
            isOpen ? "border-primary ring-2 ring-primary/20" : "hover:border-input/80",
            error ? "border-destructive ring-destructive/20" : "",
            (disabled || loading) ? "opacity-50 cursor-not-allowed bg-secondary/50" : "cursor-pointer"
          )}
          style={{ height: '40px', borderRadius: '8px' }}
        >
          <span className={cn(
            "truncate",
            !selectedOption && "text-muted-foreground/50"
          )}>
            {loading ? "Loading..." : (selectedOption ? selectedOption.label : placeholder)}
          </span>
          <ChevronDown
            size={16}
            className={cn(
              "text-muted-foreground transition-transform duration-200",
              isOpen && "rotate-180"
            )}
          />
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="absolute z-[9999] mt-1.5 max-h-72 w-full flex flex-col rounded-xl bg-popover shadow-xl border border-border overflow-hidden"
              style={{ borderRadius: '12px' }}
            >
              {searchable && (
                <div className="p-2 border-b border-border bg-popover flex items-center gap-2">
                  <Search size={14} className="text-muted-foreground" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onClick={(e) => e.stopPropagation()}
                    placeholder="Search..."
                    className="flex-1 bg-transparent border-none outline-none text-sm text-foreground placeholder:text-muted-foreground/50"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setSearchQuery(""); if (onSearch) onSearch(""); }}
                      className="p-1 hover:bg-secondary rounded-md transition-colors"
                    >
                      <X size={12} className="text-muted-foreground" />
                    </button>
                  )}
                </div>
              )}

              <div
                className="flex-1 overflow-y-auto p-1.5 custom-scrollbar max-h-60"
                onScroll={handleScroll}
              >
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => (
                    <div
                      key={option.value}
                      className={cn(
                        "cursor-pointer rounded-lg px-3 py-2 text-sm transition-all active:scale-[0.98] mb-0.5",
                        value === option.value
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-foreground hover:bg-secondary"
                      )}
                      onClick={(e) => handleOptionClick(option.value, e)}
                    >
                      {option.label}
                    </div>
                  ))
                ) : (
                  !loadingMore && (
                    <div className="px-3 py-4 text-sm text-muted-foreground italic text-center">
                      No results found
                    </div>
                  )
                )}
                {loadingMore && (
                  <div className="flex items-center justify-center py-2.5">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {error && (
        <p className="text-[11px] text-destructive mt-0.5 animate-in fade-in slide-in-from-top-1 transition-all duration-200">
          {error}
        </p>
      )}
    </div>
  );
});

CommonSelect.displayName = 'CommonSelect';

export { CommonSelect };